import { createAsyncThunk } from '@reduxjs/toolkit';

import { transcriptorApi } from '@/shared/api/transcriptor';

import {
  AUDIO_CONFIG,
  CONSULTATIONS_LIST_LIMIT,
  AddAudioChunkParams,
  FetchConsultationByIdParams,
  FetchConsultationsParams,
  OrderConsultationDocumentsParams,
  ReprocessConsultationRequest,
  StartRecordingParams,
  StopRecordingParams,
  TranscriptorApiError,
  TransferConsultationParams,
  UploadConsultationParams,
} from '../config';
import {
  blobToBase64,
  extractRecordingsFromFragments,
  extractTranscriptChunksFromFragments,
  generateObjectId,
  getEncounterDate,
  getPatientIdFromUrl,
  getProcessingProgress,
  uploadFileToPresignedUrl,
} from '../lib';

/**
 * Fetch consultations for a patient with pagination
 */
export const fetchConsultations = createAsyncThunk(
  'transcriptor/fetchConsultations',
  async (params: FetchConsultationsParams) => {
    try {
      const apiParams: Parameters<typeof transcriptorApi.getConsultations>[0] =
        {
          patient_id: params.patientId,
          include_archived: params.includeArchived,
          page: params.page,
          limit: params.limit ?? CONSULTATIONS_LIST_LIMIT,
        };

      const response = await transcriptorApi.getConsultations(apiParams);

      // Transform API response to match local types
      const consultations = response.encounters.map((encounter) => ({
        id: encounter.id,
        patientId: encounter.external_id || params.patientId,
        date: getEncounterDate(encounter),
        transcriptChunks: extractTranscriptChunksFromFragments(
          encounter.fragments,
        ),
        recordings: extractRecordingsFromFragments(encounter.fragments),
        summary: encounter.summary ?? null,
        evaluation: encounter.evaluation ?? null,
        followUp: encounter.follow_up ?? null,
        literatureReview: encounter.literature_review ?? null,
        abstract: encounter.abstract ?? null,
        status: encounter.status,
        createdBy: encounter.user_id || '',
        canViewEvaluation:
          encounter.your_permissions?.can_view_evaluation ?? false,
        canArchive: encounter.your_permissions?.can_archive ?? false,
        canRestore: encounter.your_permissions?.can_restore ?? false,
        canDelete: encounter.your_permissions?.can_delete ?? false,
        staffInfo: encounter.staff_info ?? null,
        authorUser: encounter.user ?? null,
        createdAt: new Date(encounter.created_at),
        updatedAt: new Date(encounter.updated_at),
        name: encounter.name || undefined,
        schemaCode: encounter.schema_code || undefined,
        schemaName: encounter.schema_name || undefined,
        documents: encounter.documents ?? [],
        error: encounter.error ?? null,
        audioDuration: encounter.total_audio_duration_ns
          ? Math.round(encounter.total_audio_duration_ns / 1_000_000_000)
          : undefined,
        isArchived: Boolean(encounter.archived),
      }));

      return {
        patientId: params.patientId,
        consultations,
        total: response.total,
        page: response.page,
        limit: response.limit,
      };
    } catch (error) {
      throw error instanceof TranscriptorApiError
        ? error
        : new TranscriptorApiError(
            'Failed to fetch consultations',
            'UNKNOWN_ERROR',
            error,
          );
    }
  },
);

/**
 * Fetch single consultation by ID
 */
export const fetchConsultationById = createAsyncThunk(
  'transcriptor/fetchConsultationById',
  async ({ consultationId, patientId }: FetchConsultationByIdParams) => {
    try {
      const consultation = await transcriptorApi.getConsultation(
        consultationId,
        { resolvePatientInfo: true },
      );
      const transcriptChunks = extractTranscriptChunksFromFragments(
        consultation.fragments,
      );
      const resolvedPatientId =
        patientId || getPatientIdFromUrl() || consultation.user_id || '';

      return {
        id: consultation.id,
        patientId: resolvedPatientId,
        date: getEncounterDate(consultation),
        summary: consultation.summary ?? null,
        transcriptChunks,
        recordings: extractRecordingsFromFragments(consultation.fragments),
        evaluation: consultation.evaluation ?? null,
        followUp: consultation.follow_up ?? null,
        literatureReview: consultation.literature_review ?? null,
        abstract: consultation.abstract ?? null,
        status: consultation.status,
        createdBy: consultation.user_id || '',
        canViewEvaluation:
          consultation.your_permissions?.can_view_evaluation ?? false,
        canArchive: consultation.your_permissions?.can_archive ?? false,
        canRestore: consultation.your_permissions?.can_restore ?? false,
        canDelete: consultation.your_permissions?.can_delete ?? false,
        staffInfo: consultation.staff_info ?? null,
        authorUser: consultation.user ?? null,
        createdAt: new Date(consultation.created_at),
        updatedAt: new Date(consultation.updated_at),
        audioUrl: consultation.fragments?.[0]?.audio?.url,
        error: consultation.error ?? null,
        name: consultation.name || undefined,
        schemaId: consultation.schema_id || undefined,
        schemaCode: consultation.schema_code || undefined,
        schemaName: consultation.schema_name || undefined,
        lang: consultation.lang || undefined,
        documents: consultation.documents ?? [],
        audioDuration: consultation.total_audio_duration_ns
          ? Math.round(consultation.total_audio_duration_ns / 1_000_000_000)
          : undefined,
        isArchived: Boolean(consultation.archived),
      };
    } catch (error) {
      throw error instanceof TranscriptorApiError
        ? error
        : new TranscriptorApiError(
            'Failed to fetch consultation',
            'UNKNOWN_ERROR',
            error,
          );
    }
  },
);

/**
 * Start a new recording session
 */
export const startRecordingThunk = createAsyncThunk(
  'transcriptor/startRecording',
  async (params: StartRecordingParams) => {
    try {
      const consultationId = params.encounterId ?? generateObjectId();
      const fragmentId = generateObjectId();
      const patientId = params.patientId ?? getPatientIdFromUrl();

      if (!patientId) {
        throw new TranscriptorApiError(
          'Patient ID not found in URL',
          'PATIENT_ID_NOT_FOUND',
        );
      }

      const response = await transcriptorApi.startRecording(
        consultationId,
        fragmentId,
        {
          patient_id: patientId,
          schema_id: params.schemaId,
          lang: params.language,
          ...(params.selectedOnDemandSeriesIds
            ? {
                selected_on_demand_series_ids: params.selectedOnDemandSeriesIds,
              }
            : {}),
        },
      );

      if (response.error) {
        throw new TranscriptorApiError(
          response.error,
          'START_RECORDING_ERROR',
          {
            consultationId,
            fragmentId,
          },
        );
      }

      return {
        consultationId,
        fragmentId,
      };
    } catch (error) {
      throw error instanceof TranscriptorApiError
        ? error
        : new TranscriptorApiError(
            'Failed to start recording',
            'UNKNOWN_ERROR',
            error,
          );
    }
  },
);

export const uploadConsultation = createAsyncThunk(
  'transcriptor/uploadConsultation',
  async (params: UploadConsultationParams) => {
    try {
      const patientId = params.patientId ?? getPatientIdFromUrl();

      if (!patientId && !params.encounterId) {
        throw new TranscriptorApiError(
          'Patient ID not found in URL',
          'PATIENT_ID_NOT_FOUND',
        );
      }

      // Validate file type
      const supportedTypes =
        AUDIO_CONFIG.SUPPORTED_MIME_TYPES as readonly string[];
      if (!supportedTypes.includes(params.file.type)) {
        throw new TranscriptorApiError(
          'Unsupported file type',
          'INVALID_FILE_TYPE',
          {
            fileType: params.file.type,
          },
        );
      }

      const { upload_url, consultation_id, fragment_id } = params.encounterId
        ? await transcriptorApi.getFragmentUploadUrl(params.encounterId, {
            file_name: params.file.name,
            file_size: params.file.size,
            content_type: params.file.type,
          })
        : await transcriptorApi.getUploadUrl({
            patient_id: patientId as string,
            schema_id: params.schemaId,
            lang: params.language,
            file_name: params.file.name,
            file_size: params.file.size,
            content_type: params.file.type,
            ...(params.selectedOnDemandSeriesIds
              ? {
                  selected_on_demand_series_ids:
                    params.selectedOnDemandSeriesIds,
                }
              : {}),
          });

      // Step 2: Upload file directly to storage
      await uploadFileToPresignedUrl(
        upload_url,
        params.file,
        params.onProgress,
      );

      // Step 3: Confirm upload completion
      await transcriptorApi.confirmUpload({
        consultation_id,
        fragment_id,
      });

      return { consultation_id, fragment_id };
    } catch (error) {
      if (error instanceof TranscriptorApiError) {
        throw error;
      }

      const message =
        error instanceof Error
          ? error.message
          : 'Failed to upload consultation';

      throw new TranscriptorApiError(message, 'UNKNOWN_ERROR', error);
    }
  },
);

/**
 * Add audio chunk to ongoing recording
 */
export const addAudioChunk = createAsyncThunk(
  'transcriptor/addAudioChunk',
  async (params: AddAudioChunkParams) => {
    try {
      const base64 = await blobToBase64(params.audioData);

      const response = await transcriptorApi.addAudioChunk(
        params.consultationId,
        params.fragmentId,
        {
          audio_base64: base64,
          type: params.audioData.type || AUDIO_CONFIG.DEFAULT_MIME_TYPE,
          timestamp: new Date().toISOString(),
          index: params.index,
          last: params.isLast,
        },
      );

      if (response.error) {
        throw new TranscriptorApiError(
          response.error,
          'ADD_AUDIO_CHUNK_ERROR',
          {
            consultationId: params.consultationId,
            index: params.index,
          },
        );
      }

      return response.data;
    } catch (error) {
      throw error instanceof TranscriptorApiError
        ? error
        : new TranscriptorApiError(
            'Failed to add audio chunk',
            'UNKNOWN_ERROR',
            error,
          );
    }
  },
);

/**
 * Stop recording session
 */
export const stopRecording = createAsyncThunk(
  'transcriptor/stopRecording',
  async (params: StopRecordingParams) => {
    try {
      const response = await transcriptorApi.stopRecording(
        params.consultationId,
        params.fragmentId,
        { reason: params.reason },
      );

      if (response.error) {
        throw new TranscriptorApiError(
          response.error,
          'STOP_RECORDING_ERROR',
          params,
        );
      }

      return response.data;
    } catch (error) {
      throw error instanceof TranscriptorApiError
        ? error
        : new TranscriptorApiError(
            'Failed to stop recording',
            'UNKNOWN_ERROR',
            error,
          );
    }
  },
);

/**
 * Poll consultation status for processing updates
 */
export const pollConsultationStatus = createAsyncThunk(
  'transcriptor/pollConsultationStatus',
  async (consultationId: string) => {
    try {
      const consultation =
        await transcriptorApi.getConsultation(consultationId);

      return {
        id: consultation.id,
        status: consultation.status,
        processingProgress: getProcessingProgress(consultation.status),
        transcriptChunks: extractTranscriptChunksFromFragments(
          consultation.fragments,
        ),
        summary: consultation.summary
          ? JSON.stringify(consultation.summary)
          : '',
        evaluation: consultation.evaluation
          ? JSON.stringify(consultation.evaluation)
          : undefined,
        followUp: consultation.follow_up
          ? JSON.stringify(consultation.follow_up)
          : undefined,
        literatureReview: consultation.literature_review || undefined,
      };
    } catch (error) {
      throw error instanceof TranscriptorApiError
        ? error
        : new TranscriptorApiError(
            'Failed to poll consultation status',
            'UNKNOWN_ERROR',
            error,
          );
    }
  },
);

/**
 * Reprocess failed consultation
 */
export const reprocessConsultation = createAsyncThunk(
  'transcriptor/reprocessConsultation',
  async ({
    consultationId,
    reprocessOptions,
  }: ReprocessConsultationRequest) => {
    try {
      const response = await transcriptorApi.reprocessConsultation(
        consultationId,
        reprocessOptions && { reprocess_options: reprocessOptions },
      );

      if (response.error) {
        throw new TranscriptorApiError(response.error, 'REPROCESS_ERROR', {
          consultationId,
        });
      }

      return { consultationId };
    } catch (error) {
      throw error instanceof TranscriptorApiError
        ? error
        : new TranscriptorApiError(
            'Failed to reprocess consultation',
            'UNKNOWN_ERROR',
            error,
          );
    }
  },
);

export const orderConsultationDocuments = createAsyncThunk(
  'transcriptor/orderConsultationDocuments',
  async (
    { consultationId, templateSeriesIds }: OrderConsultationDocumentsParams,
    { dispatch },
  ) => {
    try {
      const response = await transcriptorApi.reprocessConsultation(
        consultationId,
        {
          reprocess_options: {
            document_template_series_ids: templateSeriesIds,
          },
        },
      );

      if (response.error) {
        throw new TranscriptorApiError(
          response.error,
          'ORDER_DOCUMENTS_ERROR',
          { consultationId },
        );
      }

      // The stubs exist as soon as the request succeeds; refetch so the new
      // tabs show up immediately instead of waiting for the next poll.
      await dispatch(fetchConsultationById({ consultationId }));

      return { consultationId };
    } catch (error) {
      throw error instanceof TranscriptorApiError
        ? error
        : new TranscriptorApiError(
            'Failed to order consultation documents',
            'UNKNOWN_ERROR',
            error,
          );
    }
  },
);

const consultationLifecycleThunk = (
  typePrefix: string,
  call: (consultationId: string) => Promise<void>,
  failure: string,
) =>
  createAsyncThunk(typePrefix, async (consultationId: string) => {
    try {
      await call(consultationId);

      return { consultationId };
    } catch (error) {
      throw error instanceof TranscriptorApiError
        ? error
        : new TranscriptorApiError(failure, 'UNKNOWN_ERROR', error);
    }
  });

export const archiveConsultation = consultationLifecycleThunk(
  'transcriptor/archiveConsultation',
  (consultationId) => transcriptorApi.archiveConsultation(consultationId),
  'Failed to archive consultation',
);

export const restoreConsultation = consultationLifecycleThunk(
  'transcriptor/restoreConsultation',
  (consultationId) => transcriptorApi.restoreConsultation(consultationId),
  'Failed to restore consultation',
);

export const deleteConsultation = consultationLifecycleThunk(
  'transcriptor/deleteConsultation',
  (consultationId) => transcriptorApi.deleteConsultation(consultationId),
  'Failed to delete consultation',
);

/**
 * Transfer consultation to another doctor
 */
export const transferConsultation = createAsyncThunk(
  'transcriptor/transferConsultation',
  async (
    { consultationId, patientId, doctorUserId }: TransferConsultationParams,
    { dispatch },
  ) => {
    try {
      await transcriptorApi.updateConsultation(consultationId, {
        doctor_user_id: doctorUserId,
      });

      await dispatch(
        fetchConsultationById({ consultationId, patientId }),
      ).unwrap();

      return { consultationId, patientId, doctorUserId };
    } catch (error) {
      throw error instanceof TranscriptorApiError
        ? error
        : new TranscriptorApiError(
            'Failed to transfer consultation',
            'UNKNOWN_ERROR',
            error,
          );
    }
  },
);

/**
 * Finish an interrupted recording and trigger transcript generation
 */
export const finishInterruptedRecording = createAsyncThunk(
  'transcriptor/finishInterruptedRecording',
  async (params: StopRecordingParams) => {
    try {
      const response = await transcriptorApi.stopRecording(
        params.consultationId,
        params.fragmentId,
        { reason: params.reason ?? 'client_interrupted' },
      );

      if (response.error) {
        throw new TranscriptorApiError(
          response.error,
          'FINISH_INTERRUPTED_RECORDING_ERROR',
          params,
        );
      }

      return response.data;
    } catch (error) {
      throw error instanceof TranscriptorApiError
        ? error
        : new TranscriptorApiError(
            'Failed to finish interrupted recording',
            'UNKNOWN_ERROR',
            error,
          );
    }
  },
);

/**
 * Force-stop a stale consultation stuck in "recording" status.
 * Fetches the consultation to get fragmentId, then calls stopRecording API.
 */
export const forceStopStaleConsultation = createAsyncThunk(
  'transcriptor/forceStopStaleConsultation',
  async (consultationId: string) => {
    try {
      const { fragments } =
        await transcriptorApi.getConsultation(consultationId);

      if (!fragments || fragments.length === 0) {
        throw new TranscriptorApiError(
          'No fragments found for consultation',
          'NO_FRAGMENTS',
          { consultationId },
        );
      }

      const fragmentId = fragments[0].id;

      // The session that owned this recording is gone without a proper
      // finish, so the fragment is closed as interrupted, not as the user's
      // own Finish.
      const stopResponse = await transcriptorApi.stopRecording(
        consultationId,
        fragmentId,
        { reason: 'client_interrupted' },
      );

      if (stopResponse.error) {
        throw new TranscriptorApiError(stopResponse.error, 'FORCE_STOP_ERROR', {
          consultationId,
          fragmentId,
        });
      }

      return { consultationId };
    } catch (error) {
      throw error instanceof TranscriptorApiError
        ? error
        : new TranscriptorApiError(
            'Failed to force-stop stale consultation',
            'UNKNOWN_ERROR',
            error,
          );
    }
  },
);
